"use client";

import { isHoliday } from "@hyunbinseo/holidays-kr";
import * as SelectPrimitive from "@radix-ui/react-select";
import { Check, ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon } from "lucide-react";
import * as React from "react";
import { DayButton, DayPicker, type DropdownProps, getDefaultClassNames } from "react-day-picker";
import { cn } from "../../lib/utils";
import { Button, buttonVariants } from "./Button";

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  captionLayout = "label",
  buttonVariant = "ghost",
  formatters,
  components,
  ...props
}: React.ComponentProps<typeof DayPicker> & {
  buttonVariant?: React.ComponentProps<typeof Button>["variant"];
}) {
  const defaultClassNames = getDefaultClassNames();

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn(
        "bg-background group/calendar [--cell-size:2rem] in-data-[slot=card-content]:bg-transparent in-data-[slot=popover-content]:bg-transparent",
        String.raw`rtl:**:[.rdp-button\_next>svg]:rotate-180`,
        String.raw`rtl:**:[.rdp-button\_previous>svg]:rotate-180`,
        className,
      )}
      captionLayout={captionLayout}
      formatters={{
        formatMonthDropdown: date => `${date.getMonth() + 1}월`,
        ...formatters,
      }}
      classNames={{
        root: cn("w-fit", defaultClassNames.root),
        months: cn("relative flex flex-col gap-4 md:flex-row", defaultClassNames.months),
        month: cn("flex w-full flex-col gap-4", defaultClassNames.month),
        nav: cn(
          "absolute inset-x-0 top-0 flex w-full items-center justify-between gap-1 pointer-events-none",
          defaultClassNames.nav,
        ),
        button_previous: cn(
          buttonVariants({ variant: buttonVariant }),
          "h-(--cell-size) w-(--cell-size) select-none p-0 aria-disabled:opacity-50 pointer-events-auto",
          defaultClassNames.button_previous,
        ),
        button_next: cn(
          buttonVariants({ variant: buttonVariant }),
          "h-(--cell-size) w-(--cell-size) select-none p-0 aria-disabled:opacity-50 pointer-events-auto",
          defaultClassNames.button_next,
        ),
        month_caption: cn(
          "flex h-(--cell-size) w-full items-center justify-center px-(--cell-size)",
          defaultClassNames.month_caption,
        ),
        dropdowns: cn(
          "flex h-(--cell-size) w-full items-center justify-center gap-1.5 text-sm font-medium",
          defaultClassNames.dropdowns,
        ),
        dropdown_root: "",
        dropdown: "sr-only",
        caption_label: cn(
          "select-none font-medium",
          captionLayout === "label" ? "text-sm" : "sr-only",
          defaultClassNames.caption_label,
        ),
        table: "w-full border-collapse",
        weekdays: cn("flex w-full gap-1", defaultClassNames.weekdays),
        weekday: cn(
          "text-info flex-1 h-6 select-none rounded-md text-[16px] font-bold leading-[1.5] flex items-center justify-center",
          defaultClassNames.weekday,
        ),
        week: cn("mt-1 first:mt-2 flex w-full gap-1", defaultClassNames.week),
        week_number_header: cn("w-(--cell-size) select-none", defaultClassNames.week_number_header),
        week_number: cn(
          "text-muted-foreground select-none text-[0.8rem]",
          defaultClassNames.week_number,
        ),
        day: cn("group/day relative select-none p-0 text-center flex-1", defaultClassNames.day),
        range_start: cn("bg-accent rounded-l-lg", defaultClassNames.range_start),
        range_middle: cn("rounded-none", defaultClassNames.range_middle),
        range_end: cn("bg-accent rounded-r-lg", defaultClassNames.range_end),
        today: cn("rounded-lg", defaultClassNames.today),
        outside: cn("text-zinc-300 pointer-events-none", defaultClassNames.outside),
        disabled: cn("text-zinc-300 pointer-events-none", defaultClassNames.disabled),
        hidden: cn("invisible", defaultClassNames.hidden),
        ...classNames,
      }}
      components={{
        Root: ({ className, rootRef, ...props }) => {
          return <div data-slot="calendar" ref={rootRef} className={cn(className)} {...props} />;
        },
        Chevron: ({ className, orientation, ...props }) => {
          if (orientation === "left") {
            return <ChevronLeftIcon className={cn("size-4", className)} {...props} />;
          }

          if (orientation === "right") {
            return <ChevronRightIcon className={cn("size-4", className)} {...props} />;
          }

          return <ChevronDownIcon className={cn("size-4", className)} {...props} />;
        },
        Dropdown: CalendarSelectDropdown,
        DayButton: CalendarDayButton,
        WeekNumber: ({ children, ...props }) => {
          return (
            <td {...props}>
              <div className="w-full flex h-full items-center justify-center text-center">
                {children}
              </div>
            </td>
          );
        },
        ...components,
      }}
      {...props}
    />
  );
}

function CalendarDayButton({
  className,
  day,
  modifiers,
  ...props
}: React.ComponentProps<typeof DayButton>) {
  const ref = React.useRef<HTMLButtonElement>(null);
  React.useEffect(() => {
    if (modifiers.focused) ref.current?.focus();
  }, [modifiers.focused]);

  const isSunday = day.date.getDay() === 0;
  const isSelected = modifiers.selected;
  const isOutside = modifiers.outside;
  const isDisabled = modifiers.disabled;
  const isToday = modifiers.today;
  const isRedDay = (isSunday || isHoliday(day.date)) && !isSelected && !isOutside && !isDisabled;

  return (
    <button
      ref={ref}
      type="button"
      className={cn(
        "w-full aspect-square rounded-[8px] border border-transparent",
        "flex items-center justify-center",
        "text-[14px] font-bold leading-normal text-zinc-950",
        "transition-colors",
        "hover:bg-zinc-50",
        isSelected && "bg-violet-50 border-violet-500 text-violet-500 hover:bg-violet-100",
        isToday && !isSelected && "border-zinc-200",
        isRedDay && "text-red-500",
        isOutside && "text-zinc-300 pointer-events-none",
        isDisabled && "text-zinc-300 pointer-events-none",
        className,
      )}
      {...props}
    />
  );
}

function CalendarSelectDropdown({
  options,
  value,
  onChange,
  "aria-label": ariaLabel,
}: DropdownProps) {
  const handleValueChange = (newValue: string) => {
    const syntheticEvent = {
      target: { value: newValue },
    } as React.ChangeEvent<HTMLSelectElement>;
    onChange?.(syntheticEvent);
  };

  return (
    <SelectPrimitive.Root value={value?.toString()} onValueChange={handleValueChange}>
      <SelectPrimitive.Trigger
        aria-label={ariaLabel}
        className={cn(
          "flex h-8 items-center gap-1 rounded-md border border-zinc-200 bg-white",
          "px-2.5 text-sm font-medium",
          "hover:border-zinc-400",
          "focus:outline-none focus:ring-2 focus:ring-violet-500",
          "transition-colors",
          "[&>span]:line-clamp-1",
        )}
      >
        <SelectPrimitive.Value />
        <SelectPrimitive.Icon>
          <ChevronDownIcon className="size-3.5 text-zinc-500" />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>
      <SelectPrimitive.Portal>
        <SelectPrimitive.Content
          position="popper"
          className={cn(
            "relative z-50 max-h-60 min-w-24 overflow-hidden",
            "rounded-md bg-white shadow-lg ring-1 ring-zinc-200",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
            "data-[side=bottom]:slide-in-from-top-2 data-[side=top]:slide-in-from-bottom-2",
            "data-[side=bottom]:translate-y-1 data-[side=top]:-translate-y-1",
          )}
        >
          <SelectPrimitive.ScrollUpButton className="flex cursor-default items-center justify-center py-1">
            <ChevronDownIcon className="size-3.5 rotate-180" />
          </SelectPrimitive.ScrollUpButton>
          <SelectPrimitive.Viewport className="p-1">
            {options?.map(option => (
              <SelectPrimitive.Item
                key={option.value}
                value={option.value.toString()}
                disabled={option.disabled}
                className={cn(
                  "flex h-8 cursor-pointer select-none items-center justify-between",
                  "rounded px-2.5 text-sm outline-none",
                  "data-[state=checked]:bg-violet-50 data-[state=checked]:text-violet-600 data-[state=checked]:font-semibold",
                  "focus:bg-zinc-50",
                  "data-disabled:pointer-events-none data-disabled:opacity-50",
                  "transition-colors",
                )}
              >
                <SelectPrimitive.ItemText>{option.label}</SelectPrimitive.ItemText>
                <SelectPrimitive.ItemIndicator>
                  <Check className="size-3.5 text-violet-500" />
                </SelectPrimitive.ItemIndicator>
              </SelectPrimitive.Item>
            ))}
          </SelectPrimitive.Viewport>
          <SelectPrimitive.ScrollDownButton className="flex cursor-default items-center justify-center py-1">
            <ChevronDownIcon className="size-3.5" />
          </SelectPrimitive.ScrollDownButton>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  );
}

export { Calendar, CalendarDayButton };
